import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronLeft, Search, Target, X } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import {
  fetchCountries,
  fetchFeatureFlags,
  fetchFlagSegment,
  setFlagSegment,
  type AdminFeatureFlag,
  type CountryRow,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PermissionDenied } from '@/components/States'
import { cn } from '@/lib/cn'

const PLATFORMS = ['android', 'ios', 'web', 'desktop'] as const

/**
 * A14.05 — Flag auditoriya-segmenti. Bir flagni faqat tanlangan davlat/platformalarga
 * cheklaydi. Server (`GET /settings/features`) foydalanuvchining `country_code` +
 * sessiya `platform`ini BARQAROR solishtiradi → auditoriyadan tashqaridagilar
 * uchun flag «false». Bo'sh ikkala ro'yxat = cheklovsiz (qoida o'chadi). Yozuv
 * `POST /admin/settings/flags/segment` (feature_-only, audited). AUDIT_VIEW-gated.
 */
export function FlagSegments() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [params, setParams] = useSearchParams()
  const selected = params.get('key') ?? ''
  const [flags, setFlags] = useState<AdminFeatureFlag[] | null>(null)
  const [countries, setCountries] = useState<CountryRow[]>([])
  const [pickedC, setPickedC] = useState<Set<string>>(new Set())
  const [pickedP, setPickedP] = useState<Set<string>>(new Set())
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    fetchFeatureFlags({ filters: [], sort: [], page: 1, pageSize: 500, search: '', signal: new AbortController().signal })
      .then((r) => setFlags(r.rows))
      .catch(() => setFlags([]))
    fetchCountries({ filters: [], sort: [], page: 1, pageSize: 300, search: '', signal: new AbortController().signal })
      .then((r) => setCountries(r.rows))
      .catch(() => setCountries([]))
  }, [])

  const loadSegment = useCallback((key: string) => {
    if (!key) {
      setPickedC(new Set())
      setPickedP(new Set())
      return
    }
    setLoading(true)
    fetchFlagSegment(key)
      .then((seg) => {
        setPickedC(new Set(seg.countries.map((x) => x.toUpperCase())))
        setPickedP(new Set(seg.platforms.map((x) => x.toLowerCase())))
      })
      .catch(() => {
        setPickedC(new Set())
        setPickedP(new Set())
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    loadSegment(selected)
  }, [selected, loadSegment])

  const selectKey = (key: string) => setParams(key ? { key } : {}, { replace: true })

  const toggleC = (code: string) =>
    setPickedC((s) => {
      const n = new Set(s)
      n.has(code) ? n.delete(code) : n.add(code)
      return n
    })
  const toggleP = (p: string) =>
    setPickedP((s) => {
      const n = new Set(s)
      n.has(p) ? n.delete(p) : n.add(p)
      return n
    })

  const filteredCountries = useMemo(() => {
    const s = q.trim().toLowerCase()
    return s ? countries.filter((c) => `${c.code} ${c.name}`.toLowerCase().includes(s)) : countries
  }, [countries, q])

  const save = () => {
    if (!selected || busy) return
    setBusy(true)
    setFlagSegment(selected, { countries: [...pickedC], platforms: [...pickedP] })
      .then(() => toast(pickedC.size + pickedP.size === 0 ? t.flags.segCleared : t.flags.segSaved, { tone: 'success' }))
      .catch((e: unknown) => toast(t.flags.toggleFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) }))
      .finally(() => setBusy(false))
  }

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  const unrestricted = pickedC.size === 0 && pickedP.size === 0

  return (
    <>
      <PageHeader
        title={t.flags.segTitle}
        screenId="A14.05"
        actions={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.flags.title}
          </Link>
        }
      />
      <div className="border-b border-border-subtle p-3">
        <label className="mb-1 block text-xs text-tertiary">{t.flags.key}</label>
        <select
          value={selected}
          onChange={(e) => selectKey(e.target.value)}
          className="w-full max-w-md rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 font-mono text-sm text-primary outline-none"
        >
          <option value="">— {t.flags.pickFlag} —</option>
          {(flags ?? []).map((f) => (
            <option key={f.key} value={f.key}>{f.key}</option>
          ))}
        </select>
      </div>

      {!selected ? (
        <div className="flex-1 px-3 py-10 text-center text-sm text-tertiary">
          <Target size={24} className="mx-auto mb-2 opacity-60" />
          {t.flags.pickFlag}
        </div>
      ) : loading ? (
        <div className="flex-1 p-6 text-center text-sm text-tertiary">…</div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto p-3">
          <div className="max-w-3xl space-y-5">
            <p className={cn('rounded-md px-3 py-2 text-xs', unrestricted ? 'bg-surface-2 text-tertiary' : 'bg-accent-subtle text-accent')}>
              {unrestricted ? t.flags.segNoneNote : t.flags.segActiveNote}
            </p>

            {/* Platforms */}
            <div>
              <h3 className="mb-2 text-sm font-medium text-secondary">{t.flags.segPlatforms}</h3>
              <div className="flex flex-wrap gap-1.5">
                {PLATFORMS.map((p) => (
                  <button
                    key={p}
                    onClick={() => toggleP(p)}
                    className={cn(
                      'rounded-md border px-3 py-1 text-sm capitalize',
                      pickedP.has(p) ? 'border-accent bg-accent text-white' : 'border-border-default bg-surface-2 text-secondary hover:text-primary',
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Countries */}
            <div>
              <h3 className="mb-2 text-sm font-medium text-secondary">
                {t.flags.segCountries} {pickedC.size > 0 && <span className="text-tertiary">({pickedC.size})</span>}
              </h3>
              {pickedC.size > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {[...pickedC].map((code) => {
                    const c = countries.find((x) => x.code === code)
                    return (
                      <button
                        key={code}
                        onClick={() => toggleC(code)}
                        className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 text-xs text-white"
                      >
                        {c?.flag} {code}
                        <X size={12} />
                      </button>
                    )
                  })}
                </div>
              )}
              <div className="mb-2 flex items-center gap-2 rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5">
                <Search size={14} className="text-tertiary" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t.flags.segCountrySearch}
                  className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {filteredCountries.slice(0, 120).map((c) => (
                  <button
                    key={c.code}
                    onClick={() => toggleC(c.code)}
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs',
                      pickedC.has(c.code) ? 'border-accent bg-accent text-white' : 'border-border-default bg-surface-1 text-secondary hover:text-primary',
                    )}
                    title={c.name}
                  >
                    {c.flag} {c.code}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 border-t border-border-subtle pt-4">
              <button
                onClick={save}
                disabled={busy}
                className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
              >
                {t.common.save}
              </button>
              {!unrestricted && (
                <button
                  onClick={() => {
                    setPickedC(new Set())
                    setPickedP(new Set())
                  }}
                  className="rounded-md border border-border-default bg-surface-2 px-4 py-1.5 text-sm text-secondary hover:text-primary"
                >
                  {t.flags.segClear}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
